import { Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { IconButton } from "ui/icon-button";
import { Select, OptionType } from "ui/select";
import { TextField, TextFieldWithCounter } from "ui/text-field";
import CampaignFolderSelect from "components/partials/campaign-folder-select/campaign-folder-select";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import toast from "components/partials/toast/toast";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

import { useEditEmail } from "components/modals/creative-modal/hooks/use-edit-email";
import Icons from "assets";

interface EditEmailModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirmClose?: () => void;
  touchpoint: TouchpointAttributes;
}

const EditEmailModal = ({ isOpen, onClose, onConfirmClose, touchpoint }: EditEmailModalProps) => {
  const {
    methods,
    isFormValid,
    submitState,
    handleEditEmailSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    clientCampaigns,
    clientId,
    campaignId,
    selectedCampaignId,
    classTypeOptions,
    populationTypeOptions,
  } = useEditEmail(isOpen, touchpoint, onClose, onConfirmClose);

  const { control } = methods;

  const campaignOptions: OptionType[] = clientCampaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
  }));

  const handleCopyCopyCode = (copyCode?: string) => {
    navigator.clipboard.writeText(copyCode ?? "");
    toast.success({ data: { title: "Copied to clipboard" } });
  };

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<Icons.Email />}
      headline="Edit email"
      form={methods}
      onSubmit={handleEditEmailSubmit}
      submitDisabled={!isFormValid}
      submitting={submitState === "loading"}
      submitButtonText="Save changes"
      submitLoadingMessage="Saving changes...">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label="Email name"
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <CreativeFormFields
        populationTypeOptions={populationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        isContentTypeLocked={contentTypes?.length === 1}
        resetKey={selectedCampaignId}
        rowStartSlot={
          <Controller
            name="campaignId"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <Select
                {...field}
                label="Associated campaign"
                selectName="campaigns-select"
                ariaLabel="Choose campaign"
                required
                options={campaignOptions}
                value={campaignOptions.find((option) => option.value === value) ?? null}
                onChange={(option) => onChange(option?.value ?? "")}
              />
            )}
          />
        }
        afterClassSlot={
          <Controller
            name="folderId"
            control={control}
            render={({ field }) => (
              <CampaignFolderSelect
                selectName="email-folder-select"
                label="Folder name"
                placeholder=""
                ariaLabel="Choose folder"
                value={field.value}
                onChange={field.onChange}
                clientId={clientId}
                campaignId={selectedCampaignId || campaignId}
              />
            )}
          />
        }
        copyCodeSlot={
          <Controller
            name="copyCode"
            control={control}
            render={({ field, fieldState }) =>
              touchpoint.copyCode ? (
                <TextField
                  label="Copy code"
                  required
                  disabled
                  errMessage={fieldState.error?.message}
                  {...field}
                  value={field.value || ""}
                  actionRowSx={{ gap: 0 }}
                  actionTrigger={
                    <IconButton
                      variant="text"
                      aria-label="Copy to clipboard"
                      onClick={() => handleCopyCopyCode(field.value)}>
                      <Icons.ContentCopy />
                    </IconButton>
                  }
                />
              ) : (
                <TextFieldWithCounter
                  label="Copy code"
                  required
                  errMessage={fieldState.error?.message}
                  {...field}
                />
              )
            }
          />
        }
      />
    </FormDialog>
  );
};

export default EditEmailModal;
